import { useCallback, useMemo } from 'react';
import { queryOptions, useSuspenseQueries, type UseSuspenseQueryResult } from '@tanstack/react-query';
import type { GetDocumentById } from 'services/repositories/implementations/ApiDocumentServiceRepository';
import type { CustomFieldsPermissionsResponse } from './usePlanBasedPermissions';
import { planBasedPermissionsQueryConfig } from './usePlanBasedPermissions';
import { contentQueries } from 'services/content/contentQueries';
import { ContentType } from 'utils/getContentType';

export type CustomFieldsRules = {
  account: {
    canShow: boolean;
    needsUpgrade: boolean;
  };
  document: {
    canShow: boolean;
    needsUpgrade: boolean;
    canEditValues: boolean;
    canAssign: boolean;
  };
  template: {
    canShow: boolean;
    needsUpgrade: boolean;
    canEditValues: boolean;
    canAssign: boolean;
  };
};
export type CustomFieldsLevel = keyof CustomFieldsRules;

export const createEvaluation =
  (level: CustomFieldsLevel) =>
  ([permissionsResult, metadataResult]: [
    UseSuspenseQueryResult<CustomFieldsPermissionsResponse>,
    UseSuspenseQueryResult<GetDocumentById | undefined | null>
  ]) => {
    const hasPermission = !!permissionsResult.data.isCustomFieldsManagementAllowed;
    const isCustomFieldsDeniedBasedOnPlan = !!permissionsResult.data.isCustomFieldsDeniedBasedOnPlan;
    const isLocked = level === 'document' ? metadataResult.data?.isLocked : false;

    return (
      {
        account: {
          canShow: hasPermission || isCustomFieldsDeniedBasedOnPlan,
          needsUpgrade: isCustomFieldsDeniedBasedOnPlan,
        },
        document: {
          canShow: true,
          canAssign: hasPermission || isCustomFieldsDeniedBasedOnPlan,
          needsUpgrade: isCustomFieldsDeniedBasedOnPlan,
          canEditValues: !isLocked,
        },
        template: {
          canShow: hasPermission || isCustomFieldsDeniedBasedOnPlan,
          canAssign: hasPermission || isCustomFieldsDeniedBasedOnPlan,
          needsUpgrade: isCustomFieldsDeniedBasedOnPlan,
          canEditValues: false,
        },
      } satisfies CustomFieldsRules
    )[level];
  };

type UseCustomFieldRulesArgs = { level: 'account' } | { level: Exclude<CustomFieldsLevel, 'account'>; contentId: string };

export function useCustomFieldRules<T extends UseCustomFieldRulesArgs>(args: T): CustomFieldsRules[T['level']] {
  const { level } = args;
  const contentId = args.level !== 'account' ? args.contentId : undefined;

  const combine = useCallback((combinedData) => createEvaluation(level)(combinedData), [level]);

  const metadataQueryConfig = useMemo(() => {
    if (level === 'document' && contentId) {
      return contentQueries.contentMetadataById(ContentType.DOCUMENT, contentId);
    }
    return queryOptions({ queryKey: ['content-metadata-noop'], queryFn: () => null, staleTime: Infinity });
  }, [level, contentId]);

  return useSuspenseQueries({
    queries: [planBasedPermissionsQueryConfig, metadataQueryConfig],
    combine,
  }) as CustomFieldsRules[T['level']];
}
